// Translation dictionary for Cortijo Santa María landing page.
// Use the `t(key)` helper from LanguageContext to resolve nested keys.
import { ecommerceDict } from "@/i18n/ecommerce";

export const dict = {
  es: {
    meta: {
      title: "Cortijo Santa María | AOVE de cosecha temprana · Granada",
      html_lang: "es",
    },
    logo: {
      tagline: "AOVE · Granada",
    },
    nav: {
      items: [
        { href: "#historia", label: "Historia" },
        { href: "#origen", label: "Origen" },
        { href: "#producto", label: "Producto" },
        { href: "#regalo", label: "Regalo" },
        { href: "#faq", label: "FAQ" },
      ],
      cta: "Lista de espera",
      open_menu: "Abrir menú",
      lang_switch_aria: "Switch to English",
    },
    hero: {
      overline: "PRÓXIMA COSECHA · LIMONES, MOCLÍN · GRANADA",
      h1_a: "AOVE",
      h1_b: "de cosecha temprana,",
      h1_c: "próximamente",
      h1_d: "",
      body:
        "La próxima campaña está en preparación. Déjanos tu email y te avisaremos cuando la nueva edición esté disponible.",
      cta_primary: "Avísame cuando esté disponible",
      cta_secondary: "Conocer el cortijo",
      cta_whatsapp: "Hablar con nosotros",
      scarcity_prefix: "",
      scarcity_suffix: "Custodiamos la esencia del olivar desde hace más de un siglo.",
      chips: ["Picual", "Acidez < 0,4º", "500 ml · vidrio oscurecido protector"],
      note_title: "Edición limitada",
      note_quote:
        "Una finca, una cosecha, una producción limitada que marca el olivar.",
      note_author: "— Cortijo Santa María · Limones, Moclín",
      strip: [
        "Limones · Moclín · Granada",
        "Extracción en frío (menos de 27 °C)",
        "Recolección manual",
        "Próxima cosecha en preparación",
      ],
      image_alt: "Botellas de aceite de oliva virgen extra Cortijo Santa María de cosecha temprana, 500 ml y 250 ml con etiquetado propio, producto de Limones, Moclín, Granada",
      trust_payment: "Aviso de disponibilidad",
      trust_shipping: "Te informamos cuando abramos pedidos",
      trust_support: "Atención directa del cortijo",
    },
    pre_harvest: {
      overline: "PRÓXIMA COSECHA · LIMONES, MOCLÍN · GRANADA",
      title: "AOVE de cosecha temprana, próximamente",
      body: "La próxima campaña está en preparación. Déjanos tu email y te avisaremos cuando la nueva edición esté disponible.",
      cta_waitlist: "Avísame cuando esté disponible",
      product_badge: "Próxima cosecha · aún no disponible",
      trust_line: "Aviso de disponibilidad · Te informaremos cuando abramos pedidos.",
      info_note: "Información de la próxima campaña pendiente de confirmación tras cosecha, elaboración, analítica y embotellado.",
      reference_note: "Datos de referencia de la campaña anterior. Los datos definitivos de la próxima edición se confirmarán tras la cosecha, elaboración, analítica y embotellado.",
    },
    waitlist: {
      title: "Avísame cuando esté disponible",
      subtitle: "La próxima cosecha está en preparación. Te informaremos cuando la nueva edición esté disponible. Sin compromiso de compra ni reserva de unidades.",
      email_label: "Tu email",
      email_placeholder: "nombre@ejemplo.com",
      product_label: "Producto de interés (opcional)",
      product_placeholder: "Cualquier formato",
      consent_label: "Acepto recibir un aviso puntual cuando este producto esté disponible. No es un consentimiento de marketing.",
      marketing_label: "También me interesa recibir otras noticias y novedades del cortijo (opcional).",
      data_note: "Guardamos tu email, la preferencia de producto si la indicas, el idioma y la fecha y hora de solicitud. Puedes ejercer tus derechos de acceso, rectificación y supresión en cualquier momento.",
      privacy_text: "Para más información, consulta nuestra",
      privacy_link: "política de privacidad",
      privacy_text2: ".",
      submit: "Avísame",
      success_title: "Anotado",
      success_body: "Te avisaremos cuando la nueva edición esté disponible. No prometemos fecha, precio ni prioridad de compra.",
      error_generic: "Error al registrar. Inténtalo de nuevo.",
      already_registered: "Este email ya está en la lista de espera.",
      close: "Cerrar",
    },
    heritage: {
      overline: "La casa",
      h2_a: "Un proyecto familiar",
      h2_b: "con raíces en el",
      h2_c: "olivar",
      paragraphs: [
        "Un cortijo encalado y un olivar familiar en Limones, Moclín. Generaciones recorriéndolo a pie, árbol por árbol, sin más prisa que la que marca el campo.",
        "Producimos solo lo mejor que nuestros olivos pueden dar. El resto del calendario lo dedicamos a cuidar los olivos y esperar la cosecha siguiente.",
      ],
      stats: [
        { k: "1866", v: "Primer olivar" },
        { k: "100%", v: "Familiar" },
        { k: "24 h", v: "De campo a almazara" },
      ],
      caption: "Familia García Valverde · Cortijo Santa María",
      image_alt: "Vista del pueblo de Limones (Moclín, Granada) rodeado por el olivar familiar del Cortijo Santa María",
      detail_overline: "Cosecha temprana",
      detail_caption: "“Recogemos cuando el fruto vira de verde a morado. Es el momento exacto en el que el aceite tiene más vida.”",
      detail_alt: "Rama del olivar del Cortijo Santa María con aceitunas en envero, justo antes de la recolección de la cosecha temprana",
      quote:
        "“Hacemos el aceite que nos gusta tener en casa. Solo embotellamos lo que creemos que está a la altura de la cosecha.”",
      quote_author: "— Familia García Valverde",
    },
    origin: {
      overline: "Denominación de casa",
      h2_a: "Limones, Moclín.",
      h2_b: "Granada",
      paragraphs: [
        "Limones se asienta a casi 900 metros, en el norte de Granada. Inviernos fríos, veranos secos y suelos calcáreos. Un terreno alto y exigente que ralentiza la maduración y, a cambio, concentra carácter en cada aceituna.",
        "El olivar mira al sur. La sierra de Moclín lo protege del viento y la altitud le regala el tiempo que un buen fruto necesita para madurar despacio.",
      ],
      stats: [
        { k: "≈900 m", v: "Altitud" },
        { k: "Oct.–Nov.", v: "Inicio de cosecha" },
        { k: "Calcáreo", v: "Suelo" },
      ],
      badge: "Origen Granada",
      image_alt: "Olivar del Cortijo Santa María con sierra de Moclín al fondo en un día despejado de cosecha",
    },
    product: {
      overline: "El aceite",
      h2_a: "Botella 500 ml",
      h2_b: "",
      body:
        "Aceituna recogida al inicio de la maduración, molturada el mismo día. Color verde intenso, entrada herbácea y final largo a almendra. Botella de 500 ml con tapón dosificador y edición de cosecha temprana.",
      highlights: [
        "Cosecha temprana · inicio de noviembre",
        "Extracción en frío (menos de 27 °C)",
        "Botella 500 ml · vidrio oscurecido protector",
        "Tapón dosificador · edición única en cada cosecha",
      ],
      specs: [
        { k: "Variedades", v: "Picual" },
        { k: "Cosecha", v: "Temprana · inicio noviembre" },
        { k: "Extracción", v: "Extracción en frío (menos de 27 °C)" },
        { k: "Acidez", v: "< 0,4º" },
        { k: "Formato", v: "500 ml · vidrio oscurecido protector" },
        { k: "Conservación", v: "< 23 °C · sin luz directa" },
      ],
      badge: "Cosecha temprana",
      caption: "Botella 500 ml · Cortijo Santa María",
      image_alt: "Botella oscurecida protectora de AOVE Cortijo Santa María 500 ml con tapón dosificador",
      price_label: "Precio",
      price: "",
      price_unit: "",
      cta: "Descubrir esta edición",
      shipping: "Aviso de disponibilidad · Te informaremos cuando abramos pedidos.",
    },
    gift: {
      badge: "Edición regalo",
      shipping_badge: "Próxima cosecha",
      h2_a: "Dos botellas.",
      h2_b: "Una caja de madera con motivo del cortijo.",
      body:
        "Pensado para regalar. Dos botellas en caja de madera con el motivo del cortijo. Para anfitriones, familia, clientes y fechas que importan de verdad.",
      list: [
        "Caja de madera con motivo del cortijo · edición limitada",
        "Dos botellas de 500 ml · vidrio oscurecido protector",
      ],
      price_label: "Precio",
      price: "",
      price_unit: "",
      cta: "Descubrir la edición regalo",
      caption: "Caja de madera con motivo del cortijo · edición limitada",
      image_alt: "Caja regalo Cortijo Santa María con dos botellas de AOVE",
    },
    benefits: {
      overline: "La diferencia",
      h2_a: "Seis decisiones",
      h2_b: "que se",
      h2_c: "prueban en boca",
      intro:
        "Seis decisiones que tomamos al hacer este aceite. Cuestan más tiempo, producen menos litros, pero se notan, una a una, cuando lo pruebas.",
      items: [
        { title: "Cosecha temprana", desc: "Recogemos a principios de noviembre, con la aceituna aún verde. Producción limitada, más carácter en boca." },
        { title: "Extracción en frío (menos de 27 °C)", desc: "Molturamos por debajo de 27 °C y sin prisa. Conservamos el secreto del aroma verdadero." },
        { title: "Acidez < 0,4º", desc: "Muy por debajo del límite del AOVE. Limpieza en boca y vida larga en la botella." },
        { title: "Recolección manual", desc: "A mano y con vareo suave. Cada árbol a su ritmo, cada fruto sano." },
        { title: "Origen único", desc: "Un solo olivar. Sin mezclas, sin intermediarios, sin promedios." },
        { title: "Producción limitada", desc: "Disponibilidad de la próxima campaña pendiente de confirmación." },
      ],
    },
    culinary: {
      overline: "En la mesa",
      h2_a: "Cuatro formas",
      h2_b: "de",
      h2_c: "acabar un plato",
      body:
        "Nuestro AOVE no mejora un plato: lo enaltece. Mejor en crudo, justo antes de servir, sobre ingredientes simples que lo dejen hablar.",
      ritual: [
        { step: "I", title: "Tostada de pan moreno", desc: "Pan rústico, ajo frotado y sal en escama. Un hilo generoso antes de servir." },
        { step: "II", title: "Verduras a la brasa", desc: "Pimiento, berenjena y calabacín al fuego. Aceite siempre después del asado, nunca antes." },
        { step: "III", title: "Queso fresco y miel", desc: "Queso de cabra, miel de romero y unas hojas de tomillo. Aperitivo o postre." },
        { step: "IV", title: "Pescado blanco al horno", desc: "Lubina con limón. Un hilo al sacarla, nunca durante la cocción." },
      ],
      tasting_title: "Ritual de cata",
      tasting_steps: [
        "Sirve una cucharada en un vaso pequeño y tápalo con la mano.",
        "Caliéntalo con la palma y gíralo despacio para liberar los aromas.",
        "Huele, prueba y deja que el picor limpio llegue al fondo de la garganta.",
      ],
      image_alt: "Hilo de aceite de oliva sobre tostada de pan rústico",
    },
    commitment: {
      overline: "Nuestro compromiso",
      h2_a: "Lo que garantiza",
      h2_b: "cada botella",
      h2_c: "",
      items: [
        { title: "Origen único", desc: "Un solo olivar familiar en Limones, Moclín. Sin mezclas." },
        { title: "Cosecha y molturación", desc: "Aceituna recogida a mano y molturada el mismo día, en frío." },
        { title: "Trato directo", desc: "Sin intermediarios. Respondemos nosotros, desde el cortijo, por WhatsApp o email." },
      ],
    },
    shop: {
      overline: "LISTA DE ESPERA",
      h2_a: "Elige la edición",
      h2_b: "que te interesa",
      intro: "Elige tu formato y compra online con pago seguro.",
      bottle: {
        tag: "Botella individual",
        title: "AOVE · 500 ml",
        subtitle: "Cosecha temprana",
        price: "",
        price_unit: "",
        includes_title: "Incluye",
        includes: [
          "1 botella de 500 ml · vidrio oscurecido protector",
          "Tapón dosificador · edición única en cada cosecha",
        ],
        cta_primary: "Avísame cuando esté disponible",
        cta_secondary: "Ver la edición",
      },
      box: {
        tag: "Edición regalo",
        title: "Caja de madera con motivo del cortijo · 2 × 500 ml",
        subtitle: "Caja de madera con motivo del cortijo",
        price: "",
        price_unit: "",
        includes_title: "Incluye",
        includes: [
          "2 botellas de 500 ml · vidrio oscurecido protector",
          "Caja de madera con motivo del cortijo · edición limitada",
        ],
        cta_primary: "Avísame cuando esté disponible",
        cta_secondary: "Ver la edición",
      },
      most_popular: "Más elegido",
      trust_title: "Sobre nuestra próxima cosecha",
      trust_items: [
        { k: "Edición limitada", v: "Una sola cosecha al año." },
        { k: "Embalaje protegido", v: "Botellas enfundadas y caja reforzada." },
        { k: "Aviso de disponibilidad", v: "Te informaremos cuando abramos pedidos." },
        { k: "Atención directa", v: "Te atendemos nosotros, sin intermediarios." },
      ],
      order_title: "Atención directa del cortijo",
      order_body:
        "Cada pedido se confirma personalmente. Recibirás la disponibilidad, el método de pago y el seguimiento del envío. También preparamos pedidos corporativos y ediciones para empresas.",
      whatsapp_label: "WhatsApp",
      email_label: "Email",
      phone_label: "Teléfono",
      shipping_label: "Aviso",
      shipping_text: "Aviso de disponibilidad · Te informaremos cuando abramos pedidos.",
      whatsapp_message: "Hola, me gustaría recibir información sobre la próxima cosecha de aceite de Cortijo Santa María.",
      whatsapp_message_bottle:
        "Hola, me gustaría recibir un aviso cuando esté disponible la botella de 500 ml de aceite de Cortijo Santa María.",
      whatsapp_message_box:
        "Hola, me gustaría recibir un aviso cuando esté disponible el estuche de madera 2 × 500 ml de aceite de Cortijo Santa María.",
      email_subject: "Pedido · Cortijo Santa María",
      email_subject_bottle: "Pedido · Botella 500 ml · Cortijo Santa María",
      email_subject_box: "Pedido · Estuche 2 × 500 ml · Cortijo Santa María",
      email_body: "Hola,%0D%0A%0D%0AMe gustaría hacer un pedido de aceite Cortijo Santa María. Datos de envío:%0D%0A%0D%0ANombre:%0D%0ADirección:%0D%0APoblación y CP:%0D%0ATeléfono:%0D%0A%0D%0AGracias.",
    },
    mobile_cta: {
      from: "",
      price: "",
      cta: "Ver opciones",
    },
    tasting: {
      overline: "Notas de cata",
      title_a: "Verde,",
      title_b: "vivo",
      title_c: ", estructurado.",
      body: "Un perfil verde y vivo: hierba recién cortada, hoja de tomate, higuera. Y un fondo que se queda a almendra fresca cuando el aceite se va.",
      notes: [
        { label: "Vista", text: "Verde intenso con reflejos dorados. Denso al inclinar la botella." },
        { label: "Nariz", text: "Aromas herbáceos — hierba recién cortada, hoja de tomate, higuera." },
        { label: "Boca", text: "Entrada frutal y fresca. Amargor noble y picor limpio, bien integrados." },
        { label: "Final", text: "Largo y persistente, con fondo a almendra fresca y recuerdo herbáceo." },
      ],
      pairings_title: "Mejores maridajes",
      pairings: [
        "Tostadas de pan moreno con sal en escama",
        "Tomate de temporada y queso fresco",
        "Verduras a la brasa y hummus",
        "Pescados blancos y carpaccios",
        "Gazpacho, salmorejo y cremas frías",
        "Helados de vainilla y postres cítricos",
      ],
    },
    faq: {
      overline: "Preguntas",
      h2_a: "Lo que nos",
      h2_b: "preguntáis",
      h2_c: "a menudo.",
      helper_pre: "¿No encuentras tu respuesta? Escríbenos a ",
      helper_post: ".",
      items: [
        { q: "¿Cómo conservar el aceite?", a: "Guárdalo en un lugar fresco, seco y protegido de la luz directa, preferiblemente por debajo de 23 °C. No lo dejes junto al horno ni en el frigorífico. Bien conservado, conserva sus propiedades durante aproximadamente 24 meses desde el embotellado." },
        { q: "¿Cuánto dura una vez abierta la botella?", a: "Una vez abierta, recomendamos consumirla preferentemente en los dos meses siguientes para disfrutar del perfil en plenitud. El tapón dosificador ayuda a mantener la calidad entre usos." },
        { q: "¿Cuál es la diferencia con un AOVE convencional?", a: "Recogemos la aceituna verde, a inicios de noviembre, cuando rinde menos pero el aceite es más intenso y aromático. La molturación es en frío, por debajo de 27 °C, y el tiempo entre campo y almazara nunca supera las 24 horas." },
        { q: "¿Es apto para cocinar en caliente?", a: "Técnicamente sí, pero nuestro consejo es usarlo en crudo: tostadas, ensaladas, verduras, pescados y carnes justo antes de servir. En crudo es donde se aprecia todo su aroma y su picor noble." },
        { q: "¿Cuándo se cosecha y embotella?", a: "La recolección comienza a finales de octubre o principios de noviembre, al inicio de la maduración. Molturamos el mismo día y embotellamos en las semanas siguientes. La disponibilidad de cada campaña se confirmará cuando abramos pedidos." },
        { q: "¿Realizáis envíos?", a: "Sí, cuando abramos pedidos para la próxima cosecha. Te informaremos de las opciones de envío en ese momento." },
        { q: "¿Cómo se paga el pedido?", a: "Los métodos de pago se confirmarán cuando abramos pedidos para la próxima cosecha." },
        { q: "¿La caja de madera es reutilizable?", a: "Sí. La caja está pensada para conservarse y reutilizarse." },
      ],
    },
    bottlepour: {
      overline: "El oro verde",
      title_a: "Siglos de experiencia",
      title_b: "olivarera en una botella",
      body: "El aceite de Cortijo de Santa María sale de la almazara la mañana de la cosecha. Verde intenso, recién filtrado, sin retoques. Así llega a tu botella.",
      captions: [
        { k: "01 · Vidrio oscurecido protector", v: "Para que nuestro AOVE no pierda propiedades por la luz directa." },
        { k: "02 · Tapón dosificador", v: "Hilo fino para terminar un plato sin excesos." },
        { k: "03 · Embotellada en botellas de 500 ml", v: "Para comodidad de uso — limitadas al olivar familiar." },
      ],
      footer: "Limones, Moclín · Granada",
      aria: "Detalle de la botella con aceite cayendo",
      image_alt: "Botella oscurecida protectora de AOVE Cortijo Santa María 500 ml con tapón dosificador",
      video_alt: "Aceite de oliva virgen extra Cortijo Santa María recién extraído saliendo del caño de la almazara en Limones, Moclín",
      sr_desc: "Animación de la botella vertiendo aceite, progreso",
    },

    final_cta: {
      overline: "PRÓXIMA COSECHA · LIMONES, MOCLÍN · GRANADA",
      h2_a: "AOVE",
      h2_b: "de cosecha",
      h2_c: "temprana, próximamente.",
      body: "La próxima cosecha está en preparación. Déjanos tu email y te avisaremos en cuanto la nueva edición esté disponible.",
      cta_primary: "Avísame cuando esté disponible",
      cta_secondary: "Conocer el cortijo",
    },
    footer: {
      tagline: "AOVE de cosecha temprana con alma de Granada.",
      about: "Una sola almazara, un solo olivar, una sola familia. Cortijo Santa María, Limones, Moclín (Granada).",
      nav_title: "Navegación",
      nav_items: [
        { h: "/#historia", l: "La casa" },
        { h: "/#origen", l: "Origen" },
        { h: "/#producto", l: "Producto" },
        { h: "/#regalo", l: "Edición regalo" },
        { h: "/#pedido", l: "Lista de espera" },
        { h: "/#faq", l: "Preguntas" },
      ],
      contact_title: "Contacto",
      location: "Limones, Moclín",
      location_sub: "18249 Granada · España",
      mail_cta: "Escríbenos →",
      copyright: "Hecho en Granada",
      legal: [
        { l: "Aviso legal", h: "/legal/aviso" },
        { l: "Privacidad", h: "/legal/privacidad" },
        { l: "Cookies", h: "/legal/cookies" },
        { l: "Envíos", h: "/legal/envios" },
        { l: "Devoluciones", h: "/legal/devoluciones" },
        { l: "Desistir del pedido", h: "/desistimiento" },
      ],
    },
  },

  en: {
    meta: {
      title: "Cortijo Santa María | Early-harvest EVOO · Granada",
      html_lang: "en",
    },
    logo: {
      tagline: "EVOO · Granada",
    },
    nav: {
      items: [
        { href: "#historia", label: "Story" },
        { href: "#origen", label: "Origin" },
        { href: "#producto", label: "Product" },
        { href: "#regalo", label: "Gift" },
        { href: "#faq", label: "FAQ" },
      ],
      cta: "Join waitlist",
      open_menu: "Open menu",
      lang_switch_aria: "Cambiar a español",
    },
    hero: {
      overline: "NEXT HARVEST · LIMONES, MOCLÍN · GRANADA",
      h1_a: "Early-harvest",
      h1_b: "EVOO",
      h1_c: "coming soon",
      h1_d: "",
      body:
        "The next harvest is in preparation. Leave your email and we will notify you when the new edition becomes available.",
      cta_primary: "Notify me when it is available",
      cta_secondary: "Discover the estate",
      cta_whatsapp: "Chat on WhatsApp",
      scarcity_prefix: "",
      scarcity_suffix: "Estate oil, tended for over a century.",
      chips: ["Picual", "Acidity < 0.4°", "500 ml · protective dark glass"],
      note_title: "Next harvest",
      note_quote:
        "One estate. One harvest. A limited production shaped by the olive grove.",
      note_author: "— Cortijo Santa María · Limones, Moclín",
      strip: [
        "Limones · Moclín · Granada",
        "Cold extraction (below 27 °C)",
        "Hand-picked",
        "Next harvest in preparation",
      ],
      image_alt: "Cortijo Santa María extra virgin olive oil bottles, early-harvest, 500 ml and 250 ml with our own labelling, made in Limones, Moclín, Granada",
      trust_payment: "Availability notice",
      trust_shipping: "We notify when orders open",
      trust_support: "Direct support from the estate",
    },
    pre_harvest: {
      overline: "NEXT HARVEST · LIMONES, MOCLÍN · GRANADA",
      title: "Early-harvest EVOO, coming soon",
      body: "The next harvest is in preparation. Leave your email and we will notify you when the new edition becomes available.",
      cta_waitlist: "Notify me when it is available",
      product_badge: "Next harvest · not yet available",
      trust_line: "Availability notice · We will let you know when orders open.",
      info_note: "Next edition details pending confirmation after harvest, milling, laboratory analysis and bottling.",
      reference_note: "Reference data from the previous harvest. Final details for the next edition will be confirmed after harvest, production, analysis and bottling.",
    },
    waitlist: {
      title: "Notify me when available",
      subtitle: "The next harvest is in preparation. We will let you know when the new edition is available. No purchase commitment or unit reservation.",
      email_label: "Your email",
      email_placeholder: "name@example.com",
      product_label: "Product of interest (optional)",
      product_placeholder: "Any format",
      consent_label: "I agree to receive a one-time notification when this product is available. This is not marketing consent.",
      marketing_label: "I would also like to receive other news and updates from the estate (optional).",
      data_note: "We store your email, product preference if provided, language and the date and time of your request. You can exercise your rights of access, rectification and erasure at any time.",
      privacy_text: "For more information, please see our",
      privacy_link: "privacy policy",
      privacy_text2: ".",
      submit: "Notify me",
      success_title: "Noted",
      success_body: "We will notify you when the new edition is available. We make no promise of date, price or purchase priority.",
      error_generic: "Registration error. Please try again.",
      already_registered: "This email is already on the waitlist.",
      close: "Close",
    },
    heritage: {
      overline: "The house",
      h2_a: "A family project",
      h2_b: "rooted in the",
      h2_c: "olive grove",
      paragraphs: [
        "Cortijo Santa María is a family estate in Limones, Moclín. A whitewashed cortijo and an olive grove we've tended for generations. We walk it tree by tree.",
        "We make a single oil each year, from early-harvest olives. We mill slowly, without rush. The rest of the year we care for the trees and prepare the next edition.",
      ],
      stats: [
        { k: "1866", v: "First grove" },
        { k: "100%", v: "Family-run" },
        { k: "24 h", v: "Grove to mill" },
      ],
      caption: "García Valverde family · Cortijo Santa María",
      image_alt: "View of Limones village (Moclín, Granada) surrounded by Cortijo Santa María's family olive grove",
      detail_overline: "Early harvest",
      detail_caption: "“We pick when the fruit turns from green to purple. That's the exact moment when the oil has the most life.”",
      detail_alt: "Branch from Cortijo Santa María's olive grove with olives turning colour, right before the early-harvest pick",
      quote:
        "“We make the oil we want on our own table — and we only bottle what we believe honours the harvest.”",
      quote_author: "— The García Valverde family",
    },
    origin: {
      overline: "Estate origin",
      h2_a: "Limones, Moclín.",
      h2_b: "Granada",
      paragraphs: [
        "Limones, in Moclín, sits in a high, luminous and demanding landscape in northern Granada, around 900 metres of altitude. Cold winters, dry summers and calcareous soils slow the fruit's ripening and reduce yield, but they concentrate character in each olive.",
        "That landscape is what we want to carry into every bottle: grassy entry, the noble bitterness of young picual and a soft almond finish.",
      ],
      stats: [
        { k: "≈900 m", v: "Altitude" },
        { k: "Oct.–Nov.", v: "Harvest start" },
        { k: "Calcareous", v: "Soil" },
      ],
      badge: "Granada origin",
      image_alt: "Cortijo Santa María olive grove with the Moclín sierra in the background on a clear harvest day",
    },
    product: {
      overline: "The oil",
      h2_a: "500 ml bottle ·",
      h2_b: "limited edition.",
      body:
        "Green olives picked at the beginning of ripening and milled the same day. A vivid, herbaceous EVOO with freshness on the palate and a lingering almond finish. Presented in a 500 ml protective dark glass bottle with dosing cap.",
      highlights: [
        "Early harvest · early November",
        "Cold extraction (below 27 °C)",
        "500 ml bottle · protective dark glass",
        "Dosing cap · limited edition",
      ],
      specs: [
        { k: "Varieties", v: "Picual" },
        { k: "Harvest", v: "Early · early November" },
        { k: "Extraction", v: "Cold extraction (below 27 °C)" },
        { k: "Acidity", v: "< 0.4°" },
        { k: "Format", v: "500 ml · protective dark glass" },
        { k: "Storage", v: "< 23 °C · away from light" },
      ],
      badge: "Early harvest",
      caption: "500 ml bottle · Cortijo Santa María · early harvest",
      image_alt: "Cortijo Santa María 500 ml clear-glass EVOO bottle with dosing cap",
      price_label: "Price",
      price: "",
      price_unit: "",
      cta: "Discover this edition",
      shipping: "Availability notice · We will let you know when orders open.",
    },
    gift: {
      badge: "Gift edition",
      shipping_badge: "Next harvest",
      h2_a: "Two bottles.",
      h2_b: "One wooden presentation box with estate motif.",
      body:
        "An edition made for gifting. Two 500 ml bottles from our limited annual harvest in a wooden presentation box bearing the estate's motif. Designed with restraint and warmth — ready for hosts, family, clients and festive occasions.",
      list: [
        "Wooden presentation box with estate motif · limited edition",
        "Two 500 ml bottles · protective dark glass",
        "House tasting note",
        "Personalisable card with linen ribbon",
      ],
      price_label: "Price",
      price: "",
      price_unit: "",
      cta: "Discover the gift edition",
      caption: "Wooden presentation box with estate motif · limited edition",
      image_alt: "Cortijo Santa María wooden presentation box with two EVOO bottles",
    },
    benefits: {
      overline: "The difference",
      h2_a: "Six decisions",
      h2_b: "you taste",
      h2_c: "in the glass",
      intro:
        "Early harvest yields fewer litres per kilo of olives, but captures more freshness, aroma and structure. That difference is amplified by hand picking, same-day milling and the traceability of a single family estate in Limones, Moclín.",
      items: [
        { title: "Early harvest", desc: "Green olives, picked at the start of November. Intense aromas and vivid polyphenols." },
        { title: "Cold extraction (below 27 °C)", desc: "Always below 27 °C. The oil keeps all of its character." },
        { title: "Acidity < 0.4°", desc: "Well below the EVOO limit. Balance, cleanness and a long shelf life." },
        { title: "Hand-picked", desc: "By hand and with gentle poles. Respect for the tree and the healthy fruit." },
        { title: "Single origin", desc: "A single olive grove in Limones, Moclín. Full traceability, no blends." },
        { title: "Limited production", desc: "Availability for the next harvest is pending confirmation." },
      ],
    },
    culinary: {
      overline: "At the table",
      h2_a: "Four ways",
      h2_b: "to",
      h2_c: "finish a dish",
      body:
        "An oil like this doesn't sit quietly on a plate — it completes it. Use it raw, just before serving, over simple ingredients that let its green profile speak clearly.",
      ritual: [
        { step: "I", title: "Rustic bread toast", desc: "Country bread, rubbed garlic and flake salt. A generous drizzle right before serving." },
        { step: "II", title: "Grilled vegetables", desc: "Red pepper, aubergine and courgette. Always raw, after grilling." },
        { step: "III", title: "Fresh cheese and honey", desc: "Goat cheese, rosemary honey and a few thyme leaves. Dessert or appetiser." },
        { step: "IV", title: "Oven-baked white fish", desc: "Sea bass with lemon. Always finish with the oil cold, never over heat." },
      ],
      tasting_title: "Tasting ritual",
      tasting_steps: [
        "Pour a spoonful into a small glass and cover it with your hand.",
        "Warm it with your palm and swirl gently to release the aromas.",
        "Smell, taste, and let the clean pepperiness reach the back of your throat.",
      ],
      image_alt: "Stream of olive oil over a slice of rustic bread",
    },
    commitment: {
      overline: "Our commitment",
      h2_a: "What every bottle",
      h2_b: "guarantees",
      h2_c: "",
      items: [
        { title: "Single origin", desc: "One family olive grove in Limones, Moclín. Direct traceability, no blends, no middlemen." },
        { title: "Early harvest", desc: "Olives hand-picked at the start of ripening and cold-milled the same day." },
        { title: "Direct orders", desc: "We answer you personally by WhatsApp or email. Availability notice: we will let you know when orders open." },
      ],
    },
    shop: {
      overline: "WAITLIST",
      h2_a: "Choose the edition",
      h2_b: "you are interested in",
      intro:
        "Choose your format and buy online with secure payment.",
      bottle: {
        tag: "Single bottle",
        title: "EVOO · 500 ml",
        subtitle: "Early harvest",
        price: "",
        price_unit: "",
        includes_title: "Includes",
        includes: [
          "1 × 500 ml bottle · protective dark glass",
          "Dosing cap · limited edition",
          "House tasting note",
        ],
        cta_primary: "Notify me when it is available",
        cta_secondary: "View product",
      },
      box: {
        tag: "Gift edition",
        title: "Wooden box · 2 × 500 ml",
        subtitle: "Wooden presentation box with estate motif",
        price: "",
        price_unit: "",
        includes_title: "Includes",
        includes: [
          "2 × 500 ml bottles · limited edition",
          "Wooden presentation box with estate motif",
          "Personalisable card with linen ribbon",
        ],
        cta_primary: "Notify me when it is available",
        cta_secondary: "View product",
      },
      most_popular: "Most chosen",
      trust_title: "About our next harvest",
      trust_items: [
        { k: "Limited edition", v: "One harvest a year." },
        { k: "Protected packaging", v: "Sleeved bottles in reinforced box." },
        { k: "Availability notice", v: "We will notify you when orders open." },
        { k: "Direct support", v: "You speak to us, no middlemen." },
      ],
      order_title: "Direct support from the estate",
      order_body:
        "Every order is personally confirmed. You'll receive an email with availability confirmation, payment options and shipment tracking. We also prepare custom editions for corporate gifting.",
      whatsapp_label: "WhatsApp",
      email_label: "Email",
      phone_label: "Phone",
      shipping_label: "Notice",
      shipping_text: "Availability notice · We will let you know when orders open.",
      whatsapp_message: "Hello, I'd like to receive information about the next harvest of Cortijo Santa María olive oil.",
      whatsapp_message_bottle:
        "Hello, I'd like to be notified when the 500 ml bottle of Cortijo Santa María olive oil is available.",
      whatsapp_message_box:
        "Hello, I'd like to be notified when the wooden box 2 × 500 ml of Cortijo Santa María olive oil is available.",
      email_subject: "Order · Cortijo Santa María",
      email_subject_bottle: "Order · 500 ml bottle · Cortijo Santa María",
      email_subject_box: "Order · Wooden box 2 × 500 ml · Cortijo Santa María",
      email_body: "Hello,%0D%0A%0D%0AI'd like to place an order for Cortijo Santa María olive oil. Shipping details:%0D%0A%0D%0AName:%0D%0AAddress:%0D%0ACity & ZIP:%0D%0APhone:%0D%0A%0D%0AThank you.",
    },
    mobile_cta: {
      from: "",
      price: "",
      cta: "Join waitlist",
    },
    tasting: {
      overline: "Tasting notes",
      title_a: "Green,",
      title_b: "vivid",
      title_c: ", structured.",
      body: "A profile shaped by early harvest, with herbaceous freshness and notes that move toward tomato leaf, fig tree and almond. An oil made to be enjoyed raw.",
      notes: [
        { label: "Sight", text: "Deep green with gold highlights. Dense when swirled in the dark glass bottle." },
        { label: "Nose", text: "Grassy aromas — freshly cut grass, tomato leaf and fig tree." },
        { label: "Palate", text: "Fruity, fresh entry. Noble bitterness and clean pepperiness, well integrated." },
        { label: "Finish", text: "Long and persistent, a fresh almond base and a grassy memory." },
      ],
      pairings_title: "Best pairings",
      pairings: [
        "Rustic bread toast with flake salt",
        "Seasonal tomato and fresh cheese",
        "Grilled vegetables and hummus",
        "White fish and carpaccios",
        "Gazpacho, salmorejo and cold creams",
        "Vanilla ice cream and citrus desserts",
      ],
    },
    faq: {
      overline: "Questions",
      h2_a: "What you",
      h2_b: "often ask",
      h2_c: "us.",
      helper_pre: "Can't find your answer? Write to us at ",
      helper_post: ".",
      items: [
        { q: "How should I store the oil?", a: "Store it in a cool, dry place away from direct light, preferably below 23 °C. Don't keep it next to the oven or in the fridge. Properly stored, it retains its properties for about 24 months from bottling." },
        { q: "How long does it last once opened?", a: "Once opened, we recommend consuming it within the following two months to enjoy the profile at its peak. The dosing cap helps preserve quality between uses." },
        { q: "What's the difference versus a conventional EVOO?", a: "We pick the olive while still green, at the start of November, when yield is lower but the oil is more intense and aromatic. Milling is cold, below 27 °C, and the time from grove to mill never exceeds 24 hours." },
        { q: "Can it be used for hot cooking?", a: "Technically yes, but our advice is to enjoy it raw: on toast, salads, vegetables, fish and meat right before serving. That's where the aroma and noble pepperiness shine." },
        { q: "When is it harvested and bottled?", a: "Picking begins in early November, at the start of ripening. We mill the same day and bottle a few weeks later. Availability for each harvest will be confirmed when orders open." },
        { q: "Do you ship?", a: "Yes, when orders open for the next harvest. We will let you know about shipping options at that time." },
        { q: "How do I pay for the order?", a: "Payment methods will be confirmed when orders open for the next harvest." },
        { q: "Is the wooden box reusable?", a: "Yes. The box is designed to be kept and reused." },
      ],
    },
    bottlepour: {
      overline: "The gesture",
      title_a: "One bottle,",
      title_b: "one harvest",
      body: "What you see on the right is the actual oil leaving our mill in Limones the morning of the harvest. Vivid green, freshly filtered, untouched. That's how it reaches your bottle.",
      captions: [
        { k: "01 · Protective dark glass", v: "Preserves the intense green of the early harvest by keeping light off the oil." },
        { k: "02 · Dosing cap", v: "A thin pour to finish a dish without excess." },
        { k: "03 · Limited edition", v: "500 ml from our limited harvest — only from the family grove." },
      ],
      footer: "Limones, Moclín · Granada",
      aria: "Bottle detail with olive oil pouring",
      image_alt: "Cortijo Santa María 500 ml clear-glass EVOO bottle with dosing cap",
      video_alt: "Cortijo Santa María extra virgin olive oil flowing from the mill spout in Limones, Moclín, on harvest morning",
      sr_desc: "Bottle pouring oil animation, progress",
    },

    final_cta: {
      overline: "NEXT HARVEST · LIMONES, MOCLÍN · GRANADA",
      h2_a: "Early-harvest",
      h2_b: "EVOO,",
      h2_c: "coming soon.",
      body: "The next harvest is in preparation. Leave your email and we will notify you when the new edition becomes available.",
      cta_primary: "Notify me when it is available",
      cta_secondary: "Discover the estate",
    },
    footer: {
      tagline: "Early-harvest EVOO with the soul of Granada.",
      about: "A single mill, a single grove, a single family. Cortijo Santa María, Limones, Moclín (Granada, Spain).",
      nav_title: "Navigation",
      nav_items: [
        { h: "/en#historia", l: "The house" },
        { h: "/en#origen", l: "Origin" },
        { h: "/en#producto", l: "Product" },
        { h: "/en#regalo", l: "Gift edition" },
        { h: "/en#pedido", l: "Join waitlist" },
        { h: "/en#faq", l: "Questions" },
      ],
      contact_title: "Contact",
      location: "Limones, Moclín",
      location_sub: "18249 Granada · Spain",
      mail_cta: "Write to us →",
      copyright: "Made in Granada",
      legal: [
        { l: "Legal notice", h: "/legal/aviso" },
        { l: "Privacy", h: "/legal/privacidad" },
        { l: "Cookies", h: "/legal/cookies" },
        { l: "Shipping", h: "/legal/envios" },
        { l: "Returns", h: "/legal/devoluciones" },
        { l: "Withdraw order", h: "/desistimiento" },
      ],
    },
  },
};

export function resolve(lang, key) {
  const parts = key.split(".");
  const roots = [dict[lang] || dict.es, ecommerceDict[lang] || ecommerceDict.es];
  for (const root of roots) {
    let ref = root;
    let missed = false;
    for (const p of parts) {
      if (ref == null) {
        missed = true;
        break;
      }
      ref = ref[p];
    }
    if (!missed && ref != null) return ref;
  }
  return key;
}
